import { Location }                                                                                                  from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild, ElementRef, Injector } from '@angular/core';
import { ActivatedRoute, Router }                                                                                    from '@angular/router';
import {
	AppMessageHubService, AppNavigationService
}                                                                                                                    from '@cs/common/services';
import {
	WaitingForResponse
}                                                                                                                    from '@cs/common/utils';
import {
	DashboardComponent as CsDashboardComponent, DashboardDownloadButtonEventArgs, DashboardGridData
}                                                                                                                    from '@cs/components/dashboard';
import {
	FilterCompareBarQuery
}                                                                                                                    from '@cs/components/filter-and-compare-bar';
import {
	CsToastManagerService
}                                                                                                                    from '@cs/components/toast-manager';
import {
	SelectionTargetEnum,
	SelectionTargetResult
}                                                                                                                    from '@cs/core/generate';
import { convertToSelectionObjects }                                                                                 from '@cs/core/utils';
import {
	DashboardBase, DashboardConfigService, handlePostAction, onDashboardEntryClick
}                                                                                                                    from '@cs/performance-manager/dashboard';
import {
	AppService
}                                                                                                                    from '@cs/performance-manager/shared';
import {
	TabService
}                                                                                                                    from '@cs/performance-manager/tabbed-page';
import {
	UntilDestroy, untilDestroyed
}                                                                                                                    from '@ngneat/until-destroy';
import {
	TranslateService
}                                                from '@ngx-translate/core';
import { filter, takeWhile, tap }                from 'rxjs/operators';
import { ReportingQuery, ReportingStateService } from '../state';


@UntilDestroy()
@Component({
												standalone: false,
												selector:        'pmc-reporting-dashboard',
												templateUrl:     './reporting-dashboard.component.html',
												styleUrls:       ['./reporting-dashboard.component.css'],
												changeDetection: ChangeDetectionStrategy.OnPush
											})
export class ReportingDashboardComponent extends DashboardBase implements OnInit, OnDestroy {

	@ViewChild('dashboard', {static: true}) dashboard: CsDashboardComponent;
	@ViewChild('panelContentTopBar', {static: false, read: ElementRef}) panelContentTopBar: ElementRef;

	data: DashboardGridData;

	constructor(private reportingStateService: ReportingStateService,
													private dashboardConfig: DashboardConfigService,
													private injector: Injector,
													private reportingStateQuery: ReportingQuery,
													private changeDetection: ChangeDetectorRef,
													private location: Location,
													private activatedRoute: ActivatedRoute,
													protected readonly filterCompareBarQuery: FilterCompareBarQuery,
													protected readonly appNavigationService: AppNavigationService,
													protected readonly appService: AppService,
													protected readonly cdRef: ChangeDetectorRef,
													protected readonly i8n: TranslateService,
													protected readonly toastService: CsToastManagerService,
													protected readonly router: Router,
													protected readonly tabService: TabService,
													readonly appMessageHub: AppMessageHubService) {
		super(dashboardConfig, filterCompareBarQuery, appNavigationService,
								appService, cdRef, i8n, toastService, router, tabService, appMessageHub);
	}

	ngOnInit(): void {
		this.reportingStateQuery
						.select(store => store.selection)
						.pipe(untilDestroyed(this),
												takeWhile(() => this.reportingStateQuery.getValue().actAsDashboard),
												filter(value => value !== null))
						.subscribe(value => {
							this.getDashboard(value.selection);
						});

	}

	onShowDetailsButtonClick($event: SelectionTargetResult) {
		this.onDashboardEntryClick($event);
	}

	onDashboardEntryClick($event: SelectionTargetResult) {
		// Inter Panel Activity for these reports targets the REPORT filter (a form managed
		// by ReportingStateService), not the nav filter bar. So for a CurrentWindow action
		// we merge the clicked selection into the current report selection and re-run the
		// dashboard in place — this preserves the other filters (e.g. Show customers) and
		// avoids the full-page navigation/reset that triggerNavigation causes.
		const action = ($event && $event.selectionAction ? $event.selectionAction.toString() : '').toLowerCase();
		if (action === SelectionTargetEnum.CurrentWindow.toLowerCase()
			&& $event.selectionObject
			&& Object.keys($event.selectionObject).length > 0) {
			this.applyReportFilterSelection($event.selectionObject);
			return;
		}

		onDashboardEntryClick($event, this.injector, this.dashboardConfigService, this.data.name)
			.then(value => handlePostAction(value, this.injector));
	}

	/**
		* Merge an IPA selection (e.g. {IdCustomer: 1970}) into the current report form
		* selection and re-apply it, which re-runs the dashboard without navigating.
		*/
	private applyReportFilterSelection(selectionObject: {
		[key: string]: any
	}): void {
		const current = this.reportingStateQuery.getValue().selection;
		const tabname = current ? current.tabname : '';
		const merged  = current && current.selection ? {...current.selection} : {};

		// The report form lowercases the first letter of param names (IdCustomer ->
		// idCustomer), so overwrite the existing key case-insensitively instead of adding
		// a duplicate that the backend would ignore.
		Object.keys(selectionObject)
					.forEach(key => {
						const existingKey = Object.keys(merged)
																			.find(k => k.toLowerCase() === key.toLowerCase());
						merged[existingKey || key] = selectionObject[key];
					});

		this.reportingStateService.updateSelection({
			tabname:   tabname,
			selection: merged
		});
		// URL is now written centrally by reporting.component's store-selection
		// subscription (writeSelectionToUrl), so no Location.replaceState needed here.
	}

	onDownloadButtonClicked(args: DashboardDownloadButtonEventArgs) {
		const currentValue = this.reportingStateQuery.getValue();
		const dataPayload  = {
			dashboard: currentValue
															.activeReport
															.meta.name,
			selection: convertToSelectionObjects(currentValue)
		};
		Object.assign(args.selectionObject, dataPayload);
		super.onDownloadButtonClicked(args);
	}

	ngOnDestroy(): void {
	}

	private getDashboard(selection: {
		[key: string]: any
	}): void {

		// Set dashboard loading state to TRUE before fetching
		this.reportingStateService.setDashboardLoading(true);

		// Explicit start: ensure each panel receives a hard "true" before the request,
		// otherwise some panels may miss the first emission from WaitingForResponse.
		this.dashboard.panelIsLoading(true, 'broadcast');

		this.dashboardConfig.getDashboardData({
																																									dashboard: this.reportingStateQuery.getValue()
																																																					.activeReport
																																																					.meta.name,
																																									selection: convertToSelectionObjects(selection)
																																								}).pipe(tap(WaitingForResponse.new(isLoading => {
							this.dashboard.panelIsLoading(isLoading, 'broadcast');
						})))
						.subscribe(result => {
							this.data = result.value;
							// Explicit stop: WaitingForResponse.new sometimes only fires `true`
							// (on subscribe) and never the matching `false`. Force the off state
							// here so panels actually fade their loading overlay out.
							this.dashboard.panelIsLoading(false, 'broadcast');
							this.changeDetection.detectChanges();

							// Set dashboard loading state to FALSE after data loads
							this.reportingStateService.setDashboardLoading(false);

							// Notify that dashboard data has loaded (for filter collapse logic)
							this.reportingStateService.setDashboardDataLoaded(true);
						}, err => {
							// Make sure the loading overlay clears on error too.
							this.dashboard.panelIsLoading(false, 'broadcast');
							this.reportingStateService.setDashboardLoading(false);
							this.changeDetection.detectChanges();
						});
	}
}
